import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Flag, Phone, PhoneIncoming, PhoneOutgoing, Loader2 } from "lucide-react";
import { Stage } from "@/components/Stage";
import { Eyebrow } from "@/components/Wordmark";
import api from "@/lib/api";

const EASE = [0.22, 1, 0.36, 1];

const slugify = (n) => n.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const AGENTS = [
  {
    name: "Intelligent Reception",
    desc: "Answer · route · log",
    bucket: "inbound",
    dial: "+1 (412) 555-0110",
    tagline: "Answers every call. Routes every caller. Captures every detail.",
    caps: [
      "Tells new clients from adjusters and providers",
      "Routes each caller to the right place",
      "Logs structured intake to your CMS in real time",
    ],
    steps: ["Answer", "Identify", "Route", "Log"],
    outcome: "Live 24/7",
    foot: "Syncs to Litify · SmartAdvocate · Lawmatics · Salesforce",
  },
  {
    name: "Lead Qualification",
    desc: "Screen & book consults",
    bucket: "inbound",
    dial: "+1 (412) 555-0120",
    tagline: "Screens and qualifies every lead before it reaches an attorney.",
    caps: [
      "Screens for case type and viability",
      "Captures the intake details that matter",
      "Books the consult automatically",
    ],
    steps: ["Screen", "Qualify", "Capture", "Book"],
    outcome: "2–3× qualified leads to attorneys",
    foot: "Syncs to your CMS",
  },
  {
    name: "Matter Completion",
    desc: "Close matter gaps",
    bucket: "inbound",
    dial: "+1 (412) 555-0130",
    tagline: "Finds the gaps in a matter and closes them before a case stalls.",
    caps: [
      "Spots missing fields and documents",
      "Requests exactly what is needed",
      "Updates the matter as gaps close",
    ],
    steps: ["Scan", "Find gaps", "Request", "Update"],
    outcome: "No stalled files",
    foot: "Syncs to your CMS",
  },
  {
    name: "Chaser Agent",
    desc: "Chase what stalls",
    bucket: "outbound",
    tagline: "Persistent, polite outbound follow-up on everything that stalls.",
    caps: [
      "Detects stalled items automatically",
      "Follows up by call and text, on-brand",
      "Logs every attempt to the file",
    ],
    steps: ["Detect stall", "Reach out", "Follow up", "Log"],
    outcome: "Nothing falls through",
    foot: "Voice + text · logged to CMS",
  },
  {
    name: "Document Follow-up",
    desc: "Get docs back",
    bucket: "outbound",
    tagline: "Requests, reminds, and confirms until the paperwork comes back.",
    caps: [
      "Sends signature and form requests",
      "Reminds until returned",
      "Confirms receipt and files it",
    ],
    steps: ["Request", "Remind", "Receive", "Confirm"],
    outcome: "Docs returned faster",
    foot: "Syncs to your CMS",
  },
  {
    name: "Medical Records Retrieval",
    desc: "Retrieve medical records",
    bucket: "outbound",
    tagline: "Requests medical records and chases every provider to closure.",
    caps: [
      "Requests records from providers",
      "Tracks the status of each request",
      "Flags delays before they hurt a case",
    ],
    steps: ["Request", "Track", "Follow up", "Log"],
    outcome: "Fewer stalled requests",
    foot: "Provider outreach · logged to CMS",
  },
  {
    name: "Claims Opening",
    desc: "Draft claim openings",
    bucket: "outbound",
    tagline: "Assembles and drafts the claim opening — a person approves before it goes.",
    caps: [
      "Assembles the claim details",
      "Drafts the opening for review",
      "Routes to staff to approve and submit",
    ],
    steps: ["Assemble", "Draft", "Human review", "Submit"],
    outcome: "Human-in-the-loop",
    foot: "Approval required before submit",
  },
  {
    name: "Adjuster Agent",
    desc: "Handle adjuster calls",
    bucket: "inbound",
    dial: "+1 (412) 555-0140",
    tagline: "Recognizes adjuster calls, captures the details, routes what matters.",
    caps: [
      "Recognizes adjuster and carrier calls",
      "Captures offer and claim details",
      "Routes to the right attorney",
    ],
    steps: ["Recognize", "Capture", "Route", "Log"],
    outcome: "1 in 4 calls auto-routed",
    foot: "Syncs to your CMS",
  },
];

export const AgentSlide = ({ slide, onDemoCall }) => {
  const reduce = useReducedMotion();
  const [bucket, setBucket] = useState("inbound");
  const [selected, setSelected] = useState(0);
  const [activeSteps, setActiveSteps] = useState(0);
  const [showOutcome, setShowOutcome] = useState(false);
  const [flagCount, setFlagCount] = useState(0);
  const [phone, setPhone] = useState("");
  const [callStatus, setCallStatus] = useState("idle");
  const [callMsg, setCallMsg] = useState("");
  const [dialLoaded, setDialLoaded] = useState(false);
  const tabRefs = useRef([]);
  const timers = useRef([]);

  const bucketOrder = AGENTS.map((a, idx) => idx).filter(
    (idx) => AGENTS[idx].bucket === bucket
  );

  const switchBucket = (b) => {
    if (b === bucket) return;
    setBucket(b);
    setSelected(AGENTS.findIndex((a) => a.bucket === b));
    setDialLoaded(false);
    setCallStatus("idle");
    setCallMsg("");
  };

  const requestCall = async (e) => {
    e.preventDefault();
    if (callStatus === "submitting" || !phone.trim()) return;
    setCallStatus("submitting");
    setCallMsg("");
    try {
      const current = AGENTS[selected];
      const agentId = slugify(current.name);
      const res = await api.post("/deck/demo-call", {
        phone,
        agent: current?.name || "",
        agent_id: agentId,
      });
      setCallStatus("done");
      setCallMsg(res.data.message);
      if (res.data.conversation_id && onDemoCall) {
        onDemoCall({
          mode: "outbound",
          conversationId: res.data.conversation_id,
          phone,
          agentName: current?.name || "",
        });
      }
    } catch (err) {
      setCallStatus("error");
      setCallMsg(
        err?.response?.data?.detail || "Something went wrong. Please try again."
      );
    }
  };

  const loadInbound = () => {
    const current = AGENTS[selected];
    setDialLoaded(true);
    if (onDemoCall) {
      onDemoCall({
        mode: "inbound",
        agentSlug: slugify(current.name),
        agentName: current.name,
        since: Math.floor(Date.now() / 1000),
      });
    }
  };

  const agent = AGENTS[selected];
  const isReview = !!agent.flags;

  const clearTimers = () => {
    timers.current.forEach((t) => clearTimeout(t));
    timers.current = [];
  };

  // Pipeline animation on each selection.
  useEffect(() => {
    clearTimers();
    setCallStatus("idle");
    setCallMsg("");
    setDialLoaded(false);
    if (reduce) {
      setActiveSteps(agent.steps.length);
      setShowOutcome(true);
      setFlagCount(agent.flags ? agent.flags.length : 0);
      return;
    }
    setActiveSteps(0);
    setShowOutcome(false);
    setFlagCount(0);
    agent.steps.forEach((_, k) => {
      timers.current.push(setTimeout(() => setActiveSteps(k + 1), (k + 1) * 420));
    });
    const after = agent.steps.length * 420 + 320;
    if (agent.flags) {
      agent.flags.forEach((_, k) => {
        timers.current.push(setTimeout(() => setFlagCount(k + 1), after + k * 340));
      });
    } else {
      timers.current.push(setTimeout(() => setShowOutcome(true), after));
    }
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, reduce]);

  const onRailKey = useCallback(
    (e) => {
      const pos = bucketOrder.indexOf(selected);
      let nextPos = null;
      if (e.key === "ArrowDown") nextPos = (pos + 1) % bucketOrder.length;
      else if (e.key === "ArrowUp")
        nextPos = (pos - 1 + bucketOrder.length) % bucketOrder.length;
      else if (e.key === "Home") nextPos = 0;
      else if (e.key === "End") nextPos = bucketOrder.length - 1;
      if (nextPos !== null) {
        e.preventDefault();
        const next = bucketOrder[nextPos];
        setSelected(next);
        tabRefs.current[next]?.focus();
      }
    },
    [selected, bucketOrder]
  );

  const container = {
    hidden: {},
    show: { transition: { staggerChildren: reduce ? 0 : 0.12, delayChildren: 0.05 } },
  };
  const item = {
    hidden: { opacity: 0, y: reduce ? 0 : 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
  };

  return (
    <Stage testId={`slide-${slide?.n ?? 5}`}>
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="h-full w-full flex flex-col px-[clamp(28px,6%,84px)] py-[clamp(20px,4%,44px)]"
      >
        {/* Top row */}
        <motion.div variants={item} className="flex items-center justify-between">
          <Eyebrow>What we deploy</Eyebrow>
          <span className="font-mono text-xs tracking-[0.24em] text-ink-muted tabular-nums">
            06 / 11
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h1
          variants={item}
          className="font-display font-medium text-3xl sm:text-[2.1rem] leading-[1.08] text-ink mt-3"
        >
          Agents for your <span className="text-accent">bottlenecks</span>.
        </motion.h1>

        {/* Subhead */}
        <motion.p
          variants={item}
          className="font-sans text-[14px] text-ink-soft leading-relaxed mt-2.5 max-w-[60ch]"
        >
          We don't sell an AI product. We deploy the agent that fixes the part of
          your firm that's slowing you down. Pick a bottleneck →
        </motion.p>

        {/* Bucket toggle */}
        <motion.div
          variants={item}
          role="group"
          aria-label="Agent direction"
          className="mt-4 inline-flex items-center gap-1 rounded-full border border-hairline bg-[#FBFAF6] p-1 self-start"
        >
          {[
            { id: "inbound", label: "Inbound", sub: "They dial in" },
            { id: "outbound", label: "Outbound", sub: "We call them" },
          ].map((b) => {
            const on = bucket === b.id;
            return (
              <button
                key={b.id}
                type="button"
                data-testid={`bucket-${b.id}`}
                onClick={() => switchBucket(b.id)}
                className={`relative rounded-full px-4 py-1.5 transition-colors duration-200 ${
                  on ? "bg-ink text-paper-white" : "text-ink-soft hover:text-ink"
                }`}
              >
                <span className="font-sans text-[13px] font-semibold">{b.label}</span>
                <span
                  className={`ml-2 font-mono text-[10px] uppercase tracking-[0.08em] ${
                    on ? "text-paper-white/60" : "text-ink-muted"
                  }`}
                >
                  {b.sub}
                </span>
              </button>
            );
          })}
        </motion.div>

        {/* Body */}
        <div className="flex-1 min-h-0 grid grid-cols-1 min-[900px]:grid-cols-[0.72fr_1fr] gap-4 mt-5">
          {/* LEFT RAIL */}
          <motion.div
            variants={item}
            role="tablist"
            aria-label="Agents"
            aria-orientation="vertical"
            onKeyDown={onRailKey}
            className="flex flex-col gap-1.5 min-h-0"
          >
            {bucketOrder.map((i, pos) => {
              const a = AGENTS[i];
              const on = i === selected;
              return (
                <button
                  key={i}
                  ref={(el) => (tabRefs.current[i] = el)}
                  role="tab"
                  aria-selected={on}
                  tabIndex={on ? 0 : -1}
                  data-testid={`agent-tab-${pos + 1}`}
                  onClick={() => setSelected(i)}
                  className={`relative flex flex-col justify-center flex-1 min-h-0 text-left rounded-lg pl-4 pr-3 py-2 transition-colors duration-200 outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-[#F7F6F5] ${
                    on
                      ? "bg-[#FBFAF6] border border-hairline"
                      : "border border-transparent hover:bg-ink/[0.03]"
                  }`}
                >
                  {on ? (
                    <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-accent" />
                  ) : null}
                  <div className="flex items-center gap-2.5">
                    <span className="font-mono text-[11px] text-accent tabular-nums">
                      {String(pos + 1).padStart(2, "0")}
                    </span>
                    <span className="font-sans text-[13px] font-semibold text-ink leading-tight">
                      {a.name}
                    </span>
                    {a.featured ? (
                      <span className="font-mono text-[8px] uppercase tracking-[0.12em] text-accent border border-accent/40 rounded-full px-1.5 py-0.5">
                        Featured
                      </span>
                    ) : null}
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.1em] text-ink-muted mt-0.5 pl-[26px]">
                    {a.desc}
                  </div>
                </button>
              );
            })}
          </motion.div>

          {/* RIGHT DETAIL */}
          <motion.div
            variants={item}
            key={selected}
            data-testid="agent-detail"
            className="rounded-2xl border border-hairline bg-[#FBFAF6] p-6 pb-7 flex flex-col min-h-0 shadow-[0_1px_3px_rgba(35,31,35,0.04),0_12px_30px_-18px_rgba(35,31,35,0.16)]"
          >
            <div className="flex items-center gap-3">
              <span className="font-mono text-sm text-accent tabular-nums">
                {String(bucketOrder.indexOf(selected) + 1).padStart(2, "0")}
              </span>
              <h2
                data-testid="agent-name"
                className="font-display font-medium text-2xl sm:text-3xl text-ink leading-tight"
              >
                {agent.name}
              </h2>
            </div>
            <p className="font-sans text-[15px] text-accent mt-2">{agent.tagline}</p>

            <div className="mt-4 space-y-2.5">
              {agent.caps.map((c, k) => (
                <div key={k} className="flex items-start gap-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent mt-[7px] shrink-0" />
                  <span className="font-sans text-[14px] text-ink-soft leading-snug">
                    {c}
                  </span>
                </div>
              ))}
            </div>

            {/* Watch it work */}
            <div className="mt-auto pt-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-ink-muted mb-3">
                Watch it work
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {agent.steps.map((s, k) => {
                  const chipOn = k < activeSteps;
                  const arrowOn = k + 1 < activeSteps;
                  return (
                    <React.Fragment key={k}>
                      <span
                        data-testid={`pipeline-step-${k + 1}`}
                        className={`font-mono text-[11px] uppercase tracking-[0.08em] rounded-full px-3 py-1.5 border transition-all duration-300 ${
                          chipOn
                            ? "border-accent bg-accent/10 text-accent"
                            : "border-hairline text-ink-muted"
                        }`}
                      >
                        {s}
                      </span>
                      {k < agent.steps.length - 1 ? (
                        <ArrowRight
                          className={`w-3.5 h-3.5 transition-colors duration-300 ${
                            arrowOn ? "text-accent" : "text-ink-muted/40"
                          }`}
                        />
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </div>

              {/* Outcome / flags */}
              <div className="mt-3 min-h-[28px]">
                {isReview ? (
                  <div className="flex flex-wrap gap-2">
                    {agent.flags.slice(0, flagCount).map((f, k) => (
                      <motion.span
                        key={k}
                        initial={{ opacity: 0, scale: reduce ? 1 : 0.85 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: reduce ? 0.001 : 0.3, ease: EASE }}
                        data-testid={`review-flag-${k + 1}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-[#B42318]/30 bg-[#B42318]/[0.08] px-3 py-1.5"
                      >
                        <Flag className="w-3 h-3 text-[#B42318]" strokeWidth={2.5} />
                        <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-[#B42318]">
                          {f}
                        </span>
                      </motion.span>
                    ))}
                  </div>
                ) : showOutcome ? (
                  <motion.span
                    initial={{ opacity: 0, scale: reduce ? 1 : 0.85 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: reduce ? 0.001 : 0.35, ease: EASE }}
                    data-testid="agent-outcome"
                    className="inline-flex items-center rounded-full bg-accent/12 border border-accent/30 px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-[0.1em] text-accent"
                  >
                    {agent.outcome}
                  </motion.span>
                ) : null}
              </div>

              <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-ink-muted mt-3 pt-3 border-t border-hairline">
                {agent.foot}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Demo area — inbound shows a dial-in number, outbound places a call */}
        {agent.bucket === "outbound" ? (
          <>
            <motion.form
              variants={item}
              onSubmit={requestCall}
              className="mt-4 pt-4 border-t border-hairline flex flex-col sm:flex-row sm:items-center gap-3"
            >
              <div className="flex items-center gap-2.5 shrink-0">
                <PhoneOutgoing className="w-4 h-4 text-accent" />
                <span className="font-sans text-[13px] text-ink">
                  Hear it yourself — enter your number and the{" "}
                  <span className="font-semibold">{agent.name}</span> agent calls
                  you for a quick demo.
                </span>
              </div>
              <div className="flex gap-2 sm:ml-auto w-full sm:w-auto">
                <input
                  data-testid="demo-phone-input"
                  type="tel"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    if (callStatus !== "idle") {
                      setCallStatus("idle");
                      setCallMsg("");
                    }
                  }}
                  placeholder="Your phone number"
                  className="flex-1 sm:w-52 bg-transparent border-b border-hairline focus:border-accent text-ink font-mono text-sm py-2 outline-none transition-colors placeholder:text-ink-muted/50"
                />
                <button
                  type="submit"
                  data-testid="demo-call-button"
                  disabled={callStatus === "submitting"}
                  className="group inline-flex items-center justify-center gap-2 bg-ink text-paper-white font-sans text-sm font-medium px-5 py-2.5 rounded-full transition-all duration-300 hover:bg-accent disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {callStatus === "submitting" ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      Call me
                      <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                    </>
                  )}
                </button>
              </div>
            </motion.form>
            {callMsg ? (
              <p
                data-testid="demo-call-message"
                role="status"
                className={`mt-2 font-sans text-[13px] ${
                  callStatus === "error" ? "text-[#B42318]" : "text-accent"
                }`}
              >
                {callMsg}
                {callStatus === "done" ? (
                  <span className="text-ink-soft">
                    {" "}Then head to the next slide to replay your call →
                  </span>
                ) : null}
              </p>
            ) : null}
          </>
        ) : (
          <motion.div
            variants={item}
            data-testid="demo-dial-in"
            className="mt-4 pt-4 border-t border-hairline flex flex-col sm:flex-row sm:items-center gap-4"
          >
            <div className="flex items-center gap-3 shrink-0">
              <span className="w-9 h-9 rounded-full bg-accent/12 border border-accent/25 flex items-center justify-center shrink-0">
                <PhoneIncoming className="w-4 h-4 text-accent" />
              </span>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-muted">
                  Dial in to try {agent.name}
                </div>
                <a
                  href={`tel:${(agent.dial || "").replace(/[^0-9+]/g, "")}`}
                  data-testid="dial-in-number"
                  className="font-display font-medium text-xl text-ink hover:text-accent transition-colors"
                >
                  {agent.dial}
                </a>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 sm:ml-auto w-full sm:w-auto">
              <span className="font-sans text-[13px] text-ink-soft max-w-[34ch]">
                Call the number, then load your recording to replay it on the next
                slide.
              </span>
              <button
                type="button"
                data-testid="load-recording-button"
                onClick={loadInbound}
                className="group inline-flex items-center justify-center gap-2 bg-ink text-paper-white font-sans text-sm font-medium px-5 py-2.5 rounded-full transition-all duration-300 hover:bg-accent whitespace-nowrap shrink-0"
              >
                Load my recording
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
              </button>
            </div>
          </motion.div>
        )}
        {agent.bucket !== "outbound" && dialLoaded ? (
          <p
            data-testid="dial-in-message"
            role="status"
            className="mt-2 font-sans text-[13px] text-accent"
          >
            Fetching your latest call
            <span className="text-ink-soft">
              {" "}— open the next slide to hear it play →
            </span>
          </p>
        ) : null}
      </motion.div>
    </Stage>
  );
};

export default AgentSlide;
